import React from 'react'
import { motion } from 'framer-motion'
import Seo from '../components/Seo'

const TermsOfService: React.FC = () => {
  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      content: [
        'By accessing and using Fit by Cassie services, you accept and agree to be bound by these Terms of Service.',
        'If you do not agree to these terms, you may not access or use our services.',
        'We reserve the right to modify these terms at any time, and such modifications will be effective upon posting unless otherwise required by applicable law.',
        'Your continued use of our services after any such changes constitutes your acceptance of the updated terms.',
        'These terms apply to all visitors, users, clients, and others who access or use our services.'
      ]
    },
    {
      id: 'services',
      title: 'Description of Services',
      content: [
        'Fit by Cassie provides personalized fitness, strength, nutrition, physique, competition, and wellness coaching services.',
        'Services may include private training sessions, online coaching, customized training programs, nutritional guidance, accountability support, physique development, competition preparation, and digital resources.',
        'Specific services, program duration, deliverables, and coaching support vary according to the program selected at enrollment.',
        'Service availability may vary based on location, scheduling, program requirements, and coach availability.',
        'We reserve the right to modify, suspend, or discontinue any aspect of our services, subject to applicable client agreements and law.',
        'All fitness and wellness services are educational in nature and are not a substitute for professional medical advice, diagnosis, or treatment.'
      ]
    },
    {
      id: 'user-responsibilities',
      title: 'User Responsibilities',
      content: [
        'You must provide accurate, current, and complete information when enrolling in or using our services.',
        'You are responsible for maintaining the confidentiality of your account credentials.',
        'You must be at least 18 years old to independently purchase or participate in our services. Any participation by a minor requires appropriate consent from a parent or legal guardian and is subject to program eligibility.',
        'You agree to use our services only for lawful purposes and in accordance with these terms.',
        'You are responsible for your own health and safety during all fitness activities.',
        'You must inform us of any medical conditions, injuries, medications, limitations, or other circumstances that may affect your safe participation.',
        'You agree not to share, distribute, reproduce, resell, or provide unauthorized access to proprietary content or materials provided by Fit by Cassie.'
      ]
    },
    {
      id: 'payment-terms',
      title: 'Payment Terms',
      content: [
        'Payment is required according to the pricing, billing schedule, and commitment terms presented at enrollment or checkout.',
        'Certain Fit by Cassie coaching programs require a minimum commitment period. The specific price, billing frequency, minimum commitment, and minimum investment applicable to your selected program will be disclosed before purchase.',
        'The Strong Again Method™ is billed at $347 per month with an initial 3-month minimum commitment, representing a minimum investment of $1,041.',
        'After completion of the initial 3-month commitment for The Strong Again Method™, coaching continues on a month-to-month basis at $347 per month until canceled in accordance with the cancellation terms below.',
        'Fixed-term and paid-in-full coaching programs are charged according to the program length and price selected and disclosed at enrollment or checkout.',
        'By providing a payment method and completing enrollment, you authorize Fit by Cassie and its payment processor to charge the applicable amounts according to the billing terms presented at checkout.',
        'You are responsible for applicable taxes, fees, or charges associated with your purchase where required.',
        'Failure to make a required payment may result in suspension of coaching services, program access, or other services until the account is brought current.'
      ]
    }
  ]

  const cancellation = [
    {
      title: 'Minimum-Commitment Coaching',
      text: 'Programs with a stated minimum commitment remain financially committed for the entire minimum period. Cancellation during a minimum commitment does not eliminate payments or amounts due for the remainder of that commitment, except where otherwise required by applicable law or expressly agreed to in writing by Fit by Cassie. The Strong Again Method™ has an initial 3-month minimum commitment.'
    },
    {
      title: 'Month-to-Month Cancellation',
      text: 'After any applicable minimum commitment has been completed, clients may cancel future month-to-month renewal by providing notice before the next billing date. Cancellation prevents future renewals but does not ordinarily provide a refund for a billing period that has already begun, except where required by applicable law.'
    },
    {
      title: 'Fixed-Term & Paid-in-Full Programs',
      text: 'Fixed-term and paid-in-full coaching programs end according to the duration purchased unless otherwise stated at enrollment. Payments for these programs are subject to the refund terms disclosed at enrollment, these Terms, and applicable law.'
    },
    {
      title: 'Session Cancellations',
      text: 'Scheduled individual sessions should be canceled or rescheduled at least 24 hours in advance. Late cancellations or missed appointments may result in forfeiture of the scheduled session, subject to the terms of the applicable coaching program.'
    },
    {
      title: 'Refunds',
      text: 'Because coaching services, customized programming, digital materials, assessments, and other services may begin immediately after enrollment, amounts already paid are generally non-refundable once services or materials have been provided, except where required by applicable law or where Fit by Cassie expressly agrees otherwise in writing.'
    },
    {
      title: 'Emergencies & Exceptional Circumstances',
      text: 'Clients experiencing an emergency or exceptional circumstance should contact Fit by Cassie as soon as reasonably possible. Any accommodation, pause, credit, or exception will be evaluated individually and does not modify the terms applicable to other clients.'
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Terms of Service"
        description="The terms that govern your use of Fit by Cassie training, coaching, nutrition, physique, and competition services."
      />

      {/* Header band */}
      <section className="bg-ink text-white">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="eyebrow-gold justify-center mb-4">Legal</span>
            <h1 className="font-display text-4xl md:text-5xl font-semibold mb-5">
              Terms of Service
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Please read these terms carefully before using our fitness,
              nutrition, physique, and coaching services or website.
            </p>
            <div className="mt-6 text-sm text-gray-500">
              <p>Last updated: October 3, 2026</p>
              <p>Contact: Cassie Stutsman · hello@fitbycassie.com</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Document */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto px-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">
              Welcome to Fit by Cassie
            </h2>
            <div className="divider-gold mb-6" />
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") govern your use of the Fit by
              Cassie website and services operated by Cassie Stutsman ("we,"
              "our," or "us"). These Terms apply to all users of our services,
              including visitors, registered users, clients, and customers.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using our services or completing an enrollment or purchase,
              you agree to comply with and be bound by these Terms. Please
              review them carefully. If you do not agree to these Terms, you
              should not use or purchase our services.
            </p>
          </motion.div>

          {sections.map((section) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-14"
            >
              <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                {section.title}
              </h2>
              <div className="divider-gold mb-6" />
              <ul className="space-y-3">
                {section.content.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-brand rounded-full mt-2.5 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          {/* Health and Safety Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14 bg-gold-soft border border-gold/20 rounded-3xl p-8"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-5">
              Health and Safety Disclaimer
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-ink">Medical Clearance:</strong> You
                should consult with an appropriate healthcare professional
                before beginning a fitness or nutrition program, particularly
                if you have a medical condition, injury, take medication, are
                pregnant, or have concerns about your ability to participate
                safely.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-ink">Assumption of Risk:</strong> You
                understand that exercise and fitness activities involve
                inherent risks, including the risk of injury. By voluntarily
                participating, you acknowledge and assume the risks associated
                with participation to the extent permitted by applicable law.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-ink">Not Medical Advice:</strong> Fit
                by Cassie provides fitness, nutrition, wellness, and
                educational coaching. Our services do not constitute medical
                advice, diagnosis, treatment, or other licensed healthcare
                services.
              </p>
            </div>
          </motion.div>

          {/* Intellectual Property */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">
              Intellectual Property
            </h2>
            <div className="divider-gold mb-6" />
            <p className="text-gray-700 leading-relaxed mb-4">
              All content, materials, programs, resources, and intellectual
              property made available through Fit by Cassie are owned by or
              licensed to Fit by Cassie and are protected by applicable
              intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You may not reproduce, distribute, modify, publish, resell,
              share, or create derivative works from our content without
              express written permission. This includes training programs,
              nutrition materials, educational resources, coaching materials,
              digital downloads, and proprietary methodologies.
            </p>
          </motion.div>

          {/* Limitation of Liability */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">
              Limitation of Liability
            </h2>
            <div className="divider-gold mb-6" />
            <p className="text-gray-700 leading-relaxed mb-4">
              To the fullest extent permitted by applicable law, Fit by Cassie
              shall not be liable for indirect, incidental, special,
              consequential, exemplary, or punitive damages arising from or
              related to your use of our services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              To the fullest extent permitted by applicable law, our aggregate
              liability for claims arising from or related to a particular
              purchased service shall not exceed the amount you paid to Fit by
              Cassie for the specific service giving rise to the claim.
            </p>
          </motion.div>

          {/* Cancellation Policy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">
              Cancellation and Refund Policy
            </h2>
            <div className="divider-gold mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {cancellation.map((item) => (
                <div key={item.title} className="bg-cream rounded-2xl p-6">
                  <h3 className="font-semibold text-ink mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Termination */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">
              Termination
            </h2>
            <div className="divider-gold mb-6" />
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to suspend or terminate access to our
              services for nonpayment, misuse of services or materials,
              unlawful conduct, violation of these Terms, or conduct that
              threatens the safety or rights of Fit by Cassie or others,
              subject to applicable law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              A request to stop participating in or using a service does not
              eliminate payment obligations arising from an applicable minimum
              commitment or amounts already due. Provisions that by their
              nature should survive termination will remain in effect.
            </p>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-ink text-white rounded-3xl p-8 sm:p-10"
          >
            <h2 className="font-display text-2xl font-semibold mb-4">
              Questions and Contact Information
            </h2>
            <p className="text-gray-400 leading-relaxed mb-5">
              If you have questions about these Terms of Service, billing,
              cancellation, or your coaching program, please contact us:
            </p>
            <div className="space-y-1.5">
              <p className="font-medium">Fit by Cassie</p>
              <p className="text-gray-400">
                Email: hello@fitbycassie.com
              </p>
              <p className="text-gray-400">Website: fitbycassie.com</p>
            </div>
          </motion.div>

          <p className="text-gray-500 text-sm text-center mt-12">
            These Terms of Service are effective as of October 3, 2026. We
            reserve the right to update these Terms from time to time. Changes
            will be posted on this page with an updated effective date.
          </p>
        </div>
      </section>
    </div>
  )
}

export default TermsOfService
